import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SERVER_URL } from '../../../DB_Url';
import vcontrolAPI from '../../../services/documentApi';

// Helper to safely close ImageBitmap after React and Konva have unmounted it
const safeCloseBitmap = (bitmap) => {
  if (bitmap && typeof bitmap.close === 'function') {
    // 500ms allows React to unmount and Konva to flush pending renders
    setTimeout(() => {
      try {
        bitmap.close();
      } catch (e) {
        // ignore
      }
    }, 500);
  }
};

export const useImageLoader = (
  projectName,
  versionName,
  folderPath = '',
  options = {}
) => {
  const { disableImageFetch = false, hasDocuments = true } = options;
  const navigate = useNavigate();
  const location = useLocation();

  // State for the currently loaded image
  const [konvaImage, setKonvaImage] = useState(null);
  const [isReleased, setIsReleased] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [overlayImage, setOverlayImage] = useState(null); // Add state for the overlay image
  const [isOverlayLoading, setIsOverlayLoading] = useState(false);

  // State for document properties and page navigation
  const [pages, setPages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // State for page references (clickable highlights)
  const [pageReferences, setPageReferences] = useState([]);

  // State for loading and error handling
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cache for OCR results to avoid re-fetching
  const ocrCache = useRef(new Map());

  // GPU Memory Cleanup: close previous overlay ImageBitmaps when they are replaced
  const prevOverlayBitmapRef = useRef(null);
  // Sequence guard: only the latest overlay request may update state
  const overlayReqIdRef = useRef(0);

  // A page index is only meaningful inside its own project: when the project
  // changes, reset to the first page instead of reusing the previous project's
  // index (which landed users on the wrong page after switching).
  const prevProjectNameRef = useRef(projectName);

  // --- Effect 1: Fetch document info when project/excluded versions/folder change ---
  useEffect(() => {
    const projectChanged = prevProjectNameRef.current !== projectName;
    prevProjectNameRef.current = projectName;

    // HARD RESET: Clear previous data immediately when project/version changes
    // This prevents "ghosting" from previous projects while fetching new data
    setPages([]);
    setKonvaImage(null);
    setOverlayImage(null);
    setIsOverlayLoading(false);
    setPageReferences([]);

    if (!projectName) {
      return;
    }

    // When the shared project selector already knows this project has no
    // uploaded documents, skip the pointless /api/document/info round trip.
    // If the flag later flips to true (e.g. after a document upload and a
    // projects-list refresh), this effect re-runs because `hasDocuments` is a
    // dependency and the normal fetch happens then.
    if (hasDocuments === false) {
      setError(null);
      return;
    }

    const controller = new AbortController();
    const fetchDocInfo = async () => {
      try {
        const infoUrl = new URL(`${SERVER_URL}/api/document/info`);
        infoUrl.searchParams.append('project', projectName);

        // Add excluded versions if any
        if (versionName && versionName.trim()) {
          infoUrl.searchParams.append('excludeVersions', versionName);
        }

        // Add folder path if any
        if (folderPath && folderPath.trim()) {
          infoUrl.searchParams.append('folderPath', folderPath);
        }

        const response = await vcontrolAPI.fetchWithRetry(infoUrl.toString(), {
          signal: controller.signal
        });

        if (!response.ok)
          throw new Error('Failed to fetch document info.');
        const data = await response.json();
        setPages(data.pages || []);
        setCurrentImageIndex((prevIndex) => {
          const nextPages = data.pages || [];
          if (projectChanged) {
            return 0;
          }
          if (nextPages.length === 0) {
            return prevIndex;
          }
          return Math.min(prevIndex, nextPages.length - 1);
        });
        setOverlayImage(null); // Clear overlay when versions change
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message);
        setPages([]);
      }
    };

    fetchDocInfo();
    return () => controller.abort();
  }, [projectName, versionName, folderPath, hasDocuments]);

  // --- Effect 2: Fetch the actual image when the page index changes ---
  useEffect(() => {
    // Clear page references immediately when page changes
    // This ensures references are never shown without the corresponding image
    setPageReferences([]);

    // Don't fetch if disabled, no project selected, or no pages available
    if (disableImageFetch || !projectName || pages.length === 0) {
      setKonvaImage(null);
      setImageSize({ width: 0, height: 0 });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setKonvaImage(null);
    setIsReleased(false);

    const image = new window.Image();
    const imageUrl = new URL(`${SERVER_URL}/api/images`);
    imageUrl.searchParams.append('project', projectName);

    // Pass the excluded versions to the image endpoint
    if (versionName && versionName.trim()) {
      imageUrl.searchParams.append('excludeVersions', versionName);
    }

    // Pass folder path if any
    if (folderPath && folderPath.trim()) {
      imageUrl.searchParams.append('folderPath', folderPath);
    }

    imageUrl.searchParams.append('page', currentImageIndex);
    image.src = imageUrl.toString();
    // Only use Anonymous if you absolutely need to read pixel data (cropping/filters).
    // If just displaying the image, removing this can bypass the redirect CORS check.
    image.crossOrigin = null;

    image.onload = () => {
      setKonvaImage(image);
      setImageSize({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
      setIsLoading(false);
    };

    image.onerror = () => {
      setError(`Failed to load page ${currentImageIndex + 1}.`);
      setIsLoading(false);
      setKonvaImage(null);
    };

    return () => {
      // Cleanup: clear references when unmounting or before next load
      setPageReferences([]);
      image.onload = null;
      image.onerror = null;
    };
  }, [
    projectName,
    versionName,
    folderPath,
    pages,
    currentImageIndex,
    disableImageFetch
  ]);

  const clearImage = useCallback(() => {
    setKonvaImage(null);
    setIsReleased(true);
  }, []);

  // --- New Function to load an overlay image ---
  const setOverlayImageWithCleanup = useCallback((newImage) => {
    setOverlayImage((prev) => {
      // Close old ImageBitmap to release GPU memory
      if (prev && prev !== newImage) {
        safeCloseBitmap(prev);
      }
      return newImage;
    });
    prevOverlayBitmapRef.current = newImage;
  }, []);

  // Close the current overlay bitmap on unmount to release GPU memory
  useEffect(() => {
    return () => {
      // Invalidate any in-flight overlay request so it can't set state after unmount
      overlayReqIdRef.current += 1;
      const current = prevOverlayBitmapRef.current;
      if (current) {
        safeCloseBitmap(current);
        prevOverlayBitmapRef.current = null;
      }
    };
  }, []);

  const loadOverlayImage = useCallback(
    async (documentId, actualPageNumber) => {
      // Sequence guard: only the latest request may update state
      const reqId = ++overlayReqIdRef.current;

      if (!documentId || actualPageNumber === undefined) {
        setOverlayImage(null);
        setIsOverlayLoading(false);
        return;
      }

      setIsOverlayLoading(true);
      try {
        // Fetch as blob to avoid CORS redirect issues
        const blob = await vcontrolAPI.getPageImage(
          documentId,
          actualPageNumber,
        );

        // Decode off the main thread when supported so pan/zoom stays smooth
        // while the overlay image is being prepared. Fall back to an <img>
        // decode (main thread) when createImageBitmap is unavailable.
        if (typeof createImageBitmap === 'function') {
          try {
            const bitmap = await createImageBitmap(blob);
            if (reqId !== overlayReqIdRef.current) {
              // A newer request superseded this one — release the bitmap
              safeCloseBitmap(bitmap);
              return;
            }
            setOverlayImageWithCleanup(bitmap);
            setIsOverlayLoading(false);
            return;
          } catch (e) {
            // Fall through to the <img> path below
          }
        }

        const image = new window.Image();
        const url = URL.createObjectURL(blob);

        image.onload = () => {
          if (reqId !== overlayReqIdRef.current) {
            // A newer request superseded this one (or unmount happened)
            URL.revokeObjectURL(url);
            return;
          }
          setOverlayImageWithCleanup(image);
          setIsOverlayLoading(false);
          // Clean up blob URL after image loads
          URL.revokeObjectURL(url);
        };

        image.onerror = () => {
          if (reqId !== overlayReqIdRef.current) {
            URL.revokeObjectURL(url);
            return;
          }
          console.error('Failed to load overlay image.');
          setOverlayImage(null);
          setIsOverlayLoading(false);
          URL.revokeObjectURL(url);
        };

        image.src = url;
      } catch (error) {
        if (reqId !== overlayReqIdRef.current) return;
        console.error('Failed to fetch overlay image:', error);
        setOverlayImage(null);
        setIsOverlayLoading(false);
      }
    },
    [setOverlayImageWithCleanup],
  ); // This function doesn't need dependencies

  // --- Navigation Functions ---
  const goToPage = useCallback(
    (pageIndex) => {
      if (pageIndex >= 0 && pageIndex < pages.length) {
        setCurrentImageIndex(pageIndex);
      }
    },
    [pages.length],
  );

  /**
   * Navigate to a page by its label/name
   * @param {string} pageName - The page label or name to navigate to
   * @returns {boolean} - True if navigation was successful
   */
  const goToPageByName = useCallback(
    (pageName) => {
      if (!pageName || pages.length === 0) return false;

      const pageIndex = pages.findIndex(
        (p) => p.pageLabel === pageName || p.name === pageName,
      );

      if (pageIndex !== -1) {
        setCurrentImageIndex(pageIndex);
        return true;
      }
      return false;
    },
    [pages],
  );

  const goToPreviousImage = useCallback(() => {
    setCurrentImageIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNextImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      Math.min(pages.length - 1, prev + 1),
    );
  }, [pages.length]);

  // --- Page Reference Detection and Navigation ---

  /**
   * Memoized map of page keys for efficient lookup
   * Only recreated when pages array changes
   */
  const pageKeysMap = useMemo(() => {
    const map = new Map();
    pages.forEach((p) => {
      if (p.pageLabel) {
        // Extract page key: "A-101 : GROUND FLOOR PLAN" -> "A-101"
        const parts = p.pageLabel.split(' : ');
        const pageKey = parts[0].trim();
        const normalizedKey = pageKey.toUpperCase();
        map.set(normalizedKey, p.pageLabel);
      }
    });
    return map;
  }, [pages]);

  /**
   * Fetch OCR text and find page references in the current page
   * Uses caching to avoid re-fetching OCR for previously visited pages
   */
  const findPageReferences = useCallback(async (signal) => {
    const currentPageData = pages[currentImageIndex];
    const currentPageLabel = currentPageData?.pageLabel;

    // Clear references if no data or pages array is empty
    if (!currentPageData || !pages || pages.length === 0) {
      setPageReferences([]);
      return;
    }

    try {
      // Check cache first
      const cacheKey = `${currentPageData.documentId}-${currentPageData.actualPageNumber}`;
      let data;

      if (ocrCache.current.has(cacheKey)) {
        // Use cached OCR data
        data = ocrCache.current.get(cacheKey);
      } else {
        // Fetch OCR text for current page using API service
        data = await vcontrolAPI.getPageText(
          currentPageData.documentId,
          currentPageData.actualPageNumber,
          { signal }
        );

        // Cache the result
        ocrCache.current.set(cacheKey, data);
      }

      // Find OCR text blocks that match page keys
      const refs = [];
      if (data.textBlocks && Array.isArray(data.textBlocks)) {
        data.textBlocks.forEach((block) => {
          const textNormalized = block.text.trim().toUpperCase();

          if (pageKeysMap.has(textNormalized)) {
            const targetPageLabel = pageKeysMap.get(textNormalized);

            // Skip references to the current page
            if (targetPageLabel === currentPageLabel) {
              return;
            }

            const position = block.bbox || {
              x: block.x,
              y: block.y,
              width: block.width,
              height: block.height,
            };

            refs.push({
              text: block.text,
              targetPage: targetPageLabel,
              position: position,
            });
          }
        });
      }

      // console.log(`📄 Found ${refs.length} page references`);
      setPageReferences(refs);
    } catch (err) {
      console.error('Error fetching page references:', err);
      setPageReferences([]);
    }
  }, [pages, currentImageIndex, pageKeysMap]);

  useEffect(() => {
    // Determine if we should attempt to find references
    const isDataReady = pages && pages.length > 0;

    if (!isDataReady) return;

    const controller = new AbortController();

    // DEBOUNCE: Wait 250ms before fetching references
    // This prevents hitting the server when the user is flipping through pages
    const timeoutId = setTimeout(() => {
      findPageReferences(controller.signal);
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [currentImageIndex, pages, findPageReferences]);

  /**
   * Navigate to a page by updating the URL directly (creates proper history entry)
   * @param {string} pageName - The page label to navigate to
   */
  const navigateToPageReference = useCallback(
    (pageName) => {
      if (!pageName) return;

      const params = new URLSearchParams(location.search);
      params.set('page', pageName);

      // Use push (not replace) to create a new history entry
      navigate(`${location.pathname}?${params.toString()}`, {
        replace: false,
      });
    },
    [navigate, location],
  );

  return {
    konvaImage,
    isReleased,
    clearImage,
    imageSize,
    isLoading,
    error,
    overlayImage, // Return the overlay image
    isOverlayLoading, // Whether the overlay image is currently being fetched/decoded
    loadOverlayImage, // Return the function to load it
    pages,
    numImages: pages.length,
    currentImageIndex,
    currentPageData: pages[currentImageIndex],
    currentPageName: pages[currentImageIndex]?.pageLabel || '',
    goToPage,
    goToPageByName, // Navigate to a page by its label/name
    goToPreviousImage,
    goToNextImage,
    // Page reference data and navigation
    pageReferences,
    navigateToPageReference,
  };
};
